import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Shield, 
  Upload, 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileText,
  X,
  Loader2,
  AlertCircle,
  Send
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useVerificationStore, type VerificationType, type VerificationItem } from '@/stores/verificationStore';
import { useToastStore } from '@/stores/toastStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { Card, CardContent, Button, Loading, Badge, Modal } from '@/components/ui';
import { trackEvent } from '@/lib/analytics';
import { getAcquisitionEventParams } from '@/lib/acquisition';
import { VERIFICATION_LABELS, ALLOWED_DOCUMENT_TYPES, FILE_SIZE_LIMITS } from '@kulum/shared';

export function VerificationPage() {
  const { user, userData } = useAuthStore();
  const { 
    verification, 
    isLoading, 
    isUploading,
    error,
    fetchVerification,
    uploadDocument,
    removeDocument,
    submitForReview,
    clearError,
  } = useVerificationStore();
  const markVerificationNotificationsAsRead = useNotificationStore(
    (state) => state.markVerificationNotificationsAsRead
  );
  const { addToast } = useToastStore();

  const isDonor = userData?.role === 'donor';
  const isRejectedCooldownActive =
    userData?.status === 'rejected' &&
    userData.rejectedUntil instanceof Date &&
    userData.rejectedUntil.getTime() > Date.now();
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeDocumentType, setActiveDocumentType] = useState<VerificationType | null>(null);

  useEffect(() => {
    if (user?.uid) {
      void fetchVerification(user.uid);
      void markVerificationNotificationsAsRead(user.uid);
    }
  }, [user?.uid, fetchVerification, markVerificationNotificationsAsRead]);

  const handleUpload = async (type: VerificationType, file: File) => {
    if (!user?.uid) return;
    clearError();
    setActiveDocumentType(type);
    try {
      await uploadDocument(user.uid, type, file);
      addToast('success', '書類をアップロードしました');
    } catch {
      addToast('error', 'アップロードに失敗しました');
    } finally {
      setActiveDocumentType(null);
    }
  };

  const handleRemove = async (type: VerificationType, documentUrl: string) => {
    if (!user?.uid) return;
    clearError();
    setActiveDocumentType(type);
    try {
      await removeDocument(user.uid, type, documentUrl);
      addToast('success', '書類を削除しました');
    } catch {
      addToast('error', '削除に失敗しました');
    } finally {
      setActiveDocumentType(null);
    }
  };

  const handleSubmitForReview = async () => {
    if (!user?.uid) return;
    clearError();
    try {
      await submitForReview(user.uid);
      if (!isDonor) {
        const identityDocumentCount = verification?.identity?.documents?.length || 0;
        trackEvent('donee_identity_submit', {
          source: 'verification_page',
          role: 'donee',
          verification_type: 'identity',
          document_count: identityDocumentCount,
          identity_status_before: verification?.identity?.status || 'none',
          ...getAcquisitionEventParams(),
        });
      }
      addToast('success', '審査を申請しました');
      setShowSubmitModal(false);
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : '申請に失敗しました';
      addToast('error', message);
    }
  };

  // Define verification items based on role
  const verificationItems: { type: VerificationType; label: string; description: string; required: boolean }[] = [
    { 
      type: 'identity', 
      label: VERIFICATION_LABELS.identity,
      description: '運転免許証、パスポート、マイナンバーカードなど',
      required: true,
    },
    ...(isDonor ? [
      { 
        type: 'education' as VerificationType, 
        label: VERIFICATION_LABELS.education,
        description: '卒業証明書、学位記など',
        required: true,
      },
      { 
        type: 'income' as VerificationType, 
        label: VERIFICATION_LABELS.income,
        description: '源泉徴収票、確定申告書など',
        required: true,
      },
      { 
        type: 'spermTest' as VerificationType, 
        label: VERIFICATION_LABELS.spermTest,
        description: '精液検査の結果書類',
        required: true,
      },
      { 
        type: 'stdTest' as VerificationType, 
        label: VERIFICATION_LABELS.stdTest,
        description: '性病検査の陰性証明書（3ヶ月以内）',
        required: true,
      },
    ] : []),
  ];

  const canSubmitByReviewStatus =
    !verification?.adminReview ||
    verification.adminReview.status === 'rejected' ||
    verification.adminReview.status === 'remanded';
  const allRequiredDocumentsUploaded = verificationItems.every((item) => {
    if (!item.required) return true;
    const docs = verification?.[item.type]?.documents;
    return docs && docs.length > 0;
  });
  const canOpenSubmitModal = canSubmitByReviewStatus && !isRejectedCooldownActive;
  const isDocumentActionRunning = isUploading && activeDocumentType !== null;
  const errorMessages = (error ?? '')
    .split('\n')
    .map((message) => message.trim())
    .filter((message) => message.length > 0);

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F4EF]">
        <div className="px-4 py-16 md:mx-auto md:max-w-3xl md:px-6">
          <Loading message="読み込み中..." />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-full bg-[#F7F4EF]">
      <div className="md:mx-auto md:max-w-3xl md:px-6 md:py-6">
        <div className="px-4 pb-12 pt-14 md:px-0 md:pt-0">
          <Link
            to="/mypage"
            className="mb-5 inline-flex items-center gap-2 text-[13px] text-[#7E7668] transition hover:text-copper"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>マイページに戻る</span>
          </Link>

          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#B7B0A4]">Verification</p>
              <h1 className="mt-1 text-[22px] font-semibold text-[#1A1A1A]">認証バッジ管理</h1>
            </div>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#F5EDE2]">
              <Shield className="h-5 w-5 text-copper" />
            </div>
          </div>

          {/* Admin Review Status */}
          {verification?.adminReview && (
            <Card className="mb-6 overflow-hidden border-[#E9D8C3] bg-[#FBF5EC]">
              <CardContent className="flex items-center gap-4 px-5 py-4">
                {verification.adminReview.status === 'pending' && (
                  <>
                    <Clock className="h-6 w-6 text-yellow-600" />
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A]">審査中</h3>
                      <p className="text-sm text-[#7E7668]">
                        書類の審査には通常2〜3営業日かかります。
                      </p>
                    </div>
                  </>
                )}
                {verification.adminReview.status === 'approved' && (
                  <>
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A]">審査完了</h3>
                      <p className="text-sm text-[#7E7668]">
                        審査が完了し、本会員として承認されました。
                      </p>
                    </div>
                  </>
                )}
                {verification.adminReview.status === 'rejected' && (
                  <>
                    <XCircle className="h-6 w-6 text-red-600" />
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A]">審査却下</h3>
                      <p className="text-sm text-[#7E7668]">
                        {verification.adminReview.comment || '書類に不備がありました。再度アップロードしてください。'}
                      </p>
                    </div>
                  </>
                )}
                {verification.adminReview.status === 'remanded' && (
                  <>
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                    <div>
                      <h3 className="font-semibold text-[#1A1A1A]">差し戻し</h3>
                      <p className="text-sm text-[#7E7668]">
                        {verification.adminReview.comment || '入力内容と書類の照合に不備がありました。修正して再申請してください。'}
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Info card */}
          <Card className="mb-6 overflow-hidden border-[#E3D7C7] bg-[#F5EDE2]">
            <CardContent className="flex gap-3 px-5 py-4">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-copper" />
              <p className="text-sm leading-relaxed text-[#6F6658]">
                認証バッジを取得すると、プロフィールの信頼性が向上し、マッチング率がアップします。
                {isDonor && '本人確認と審査が完了すると、ドニーからのメッセージを受けられるようになります。'}
              </p>
            </CardContent>
          </Card>

          {/* Verification Items */}
          <div className="space-y-4">
            {verificationItems.map((item) => (
              <VerificationItemCard
                key={item.type}
                type={item.type}
                label={item.label}
                description={item.description}
                required={item.required}
                data={verification?.[item.type]}
                onUpload={(file) => handleUpload(item.type, file)}
                onRemove={(url) => handleRemove(item.type, url)}
                isUploading={isUploading && activeDocumentType === item.type}
                isBusy={isDocumentActionRunning}
                suppressRejectionFeedback={verification?.adminReview?.status === 'remanded'}
              />
            ))}
          </div>

          <div className="mt-8">
            <Button
              onClick={() => setShowSubmitModal(true)}
              disabled={!canOpenSubmitModal || isUploading || !allRequiredDocumentsUploaded}
              className="!h-12 w-full"
              leftIcon={<Send className="h-4 w-4" />}
            >
              {isDonor ? '審査を申請する' : '本人確認を申請する'}
            </Button>
            {(!canOpenSubmitModal || !allRequiredDocumentsUploaded) && (
              <p className="mt-2 text-center text-sm text-neutral-500">
                {isRejectedCooldownActive
                  ? `再申請は ${userData?.rejectedUntil?.toLocaleDateString('ja-JP')} 以降に可能です`
                  : !canSubmitByReviewStatus
                  ? '審査結果の反映をお待ちください'
                  : !allRequiredDocumentsUploaded
                  ? 'すべての必須書類をアップロードしてください'
                  : '審査情報を確認してください'}
              </p>
            )}
            {errorMessages.length > 0 && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3">
                <p className="text-sm font-medium text-red-700">申請内容を確認してください。</p>
                <ul className="mt-1 list-disc pl-5 text-sm text-red-700">
                  {errorMessages.map((message, index) => (
                    <li key={`${message}-${index}`}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Submit Confirmation Modal */}
      <Modal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        title="審査を申請"
      >
        <div className="space-y-4">
          <p className="text-neutral-600">
            アップロードした書類で審査を申請します。審査には通常2〜3営業日かかります。
          </p>
          <p className="text-sm text-neutral-500">
            審査中は書類の追加・削除はできません。
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowSubmitModal(false)}>
              キャンセル
            </Button>
            <Button onClick={handleSubmitForReview} disabled={isUploading}>
              {isUploading ? '送信中...' : '申請する'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Verification Item Card Component
interface VerificationItemCardProps {
  type: VerificationType;
  label: string;
  description: string;
  required: boolean;
  data?: VerificationItem;
  onUpload: (file: File) => void;
  onRemove: (url: string) => void;
  isUploading: boolean;
  isBusy: boolean;
  suppressRejectionFeedback: boolean;
}

function VerificationItemCard({
  type,
  label,
  description,
  required,
  data,
  onUpload,
  onRemove,
  isUploading,
  isBusy,
  suppressRejectionFeedback,
}: VerificationItemCardProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const status = data?.status || 'none';
  const effectiveStatus =
    suppressRejectionFeedback && status === 'rejected' ? 'none' : status;
  const documents = data?.documents || [];
  const hasUploadedDocuments = documents.length > 0;

  const handleFileSelect = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    // Validate file type
    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type as typeof ALLOWED_DOCUMENT_TYPES[number])) {
      setLocalError('サポートされていないファイル形式です。JPEG、PNG、GIF、WebP、PDFのみ対応しています。');
      return;
    }

    // Validate file size
    if (file.size > FILE_SIZE_LIMITS.VERIFICATION_DOCUMENT) {
      setLocalError('ファイルサイズは10MB以下にしてください');
      return;
    }

    setLocalError(null);
    onUpload(file);
  };

  const getStatusIcon = () => {
    switch (effectiveStatus) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        if (hasUploadedDocuments) {
          return <FileText className="w-5 h-5 text-copper" />;
        }
        return <Shield className="w-5 h-5 text-neutral-300" />;
    }
  };

  const getStatusBadge = () => {
    switch (effectiveStatus) {
      case 'approved':
        return <Badge variant="success">認証済み</Badge>;
      case 'pending':
        return <Badge variant="warning">審査待ち</Badge>;
      case 'rejected':
        return <Badge variant="error">要修正</Badge>;
      default:
        if (hasUploadedDocuments) {
          return <Badge variant="default">選択済み</Badge>;
        }
        return <Badge variant="default">未提出</Badge>;
    }
  };

  return (
    <Card className="overflow-hidden border-[#ECE6DD] bg-white">
      <CardContent className="px-5 py-5">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-[#F5F2ED]">
            {getStatusIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-[#1A1A1A]">{label}</h3>
              {required && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-500">必須</span>}
              {getStatusBadge()}
            </div>
            <p className="mb-3 text-sm text-[#7E7668]">{description}</p>

            {/* Rejection reason */}
            {effectiveStatus === 'rejected' && data?.rejectionReason && (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-700">{data.rejectionReason}</p>
              </div>
            )}

            {/* Uploaded documents */}
            {documents.length > 0 && (
              <div className="mb-3 space-y-2">
                {documents.map((doc) => (
                  <div
                    key={doc.url}
                    className="flex items-center gap-2 rounded-xl border border-[#F0EDE8] bg-[#FCFBF9] p-2.5"
                  >
                    <FileText className="w-4 h-4 text-neutral-500" />
                    <span className="text-sm text-neutral-700 truncate flex-1">
                      {doc.fileName}
                    </span>
                    {effectiveStatus !== 'approved' && (
                      <button
                        onClick={() => onRemove(doc.url)}
                        disabled={isBusy}
                        className="p-1 text-neutral-400 hover:text-red-500 transition disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {type === 'spermTest' && effectiveStatus === 'approved' && data?.rank && (
              <p className="mb-3 text-xs text-neutral-600">
                精液検査ランク: <span className="font-semibold">{data.rank}</span>
              </p>
            )}

            {/* Upload button */}
            {effectiveStatus !== 'approved' && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleFileSelect}
                  disabled={isBusy}
                  className="!rounded-full !border-[#E5DED3] !bg-white !text-[#5F584D]"
                  leftIcon={isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                >
                  {isUploading ? 'アップロード中...' : '書類をアップロード'}
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ALLOWED_DOCUMENT_TYPES.join(',')}
                  onChange={handleFileChange}
                  className="hidden"
                />
              </>
            )}

            {/* Local error */}
            {localError && (
              <p className="mt-2 text-sm text-red-500">{localError}</p>
            )}

            {/* Expiry info for tests */}
            {(type === 'stdTest' || type === 'spermTest') && effectiveStatus === 'approved' && data?.expiresAt && (
              <p className="mt-2 text-xs text-neutral-500">
                有効期限: {new Date(data.expiresAt).toLocaleDateString('ja-JP')}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
